import React, { useEffect, useState, useContext } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { AuthContext } from '../../AuthContext';
import PlanServices from '../../services/PlanServices';
import './Profile.css';

const DAY_MS = 24 * 60 * 60 * 1000;
const fmtDate = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
};
const shown = (v) => (v === null || v === undefined || String(v).trim() === '' ? '—' : v);

// Subscription to show: the latest one from the history (or the active one), else the plan
// dates stored on the user. Status is Active while the end date is in the future.
const pickSubscription = (profile, active, history) => {
  const list = [active, ...(Array.isArray(history) ? history : [])].filter(Boolean);
  const latest = list.sort((a, b) => new Date(b.end_date || b.endDate) - new Date(a.end_date || a.endDate))[0];
  const start = latest?.start_date || latest?.startDate || profile?.plan_start_date;
  const end = latest?.end_date || latest?.endDate || profile?.plan_end_date;
  if (!start && !end) return null;
  const endTime = end ? new Date(end).getTime() : NaN;
  const cancelled = String(latest?.status || '').toLowerCase() === 'cancelled';
  const isActive = !cancelled && Number.isFinite(endTime) && endTime >= Date.now();
  return {
    planName: latest?.plan_name || latest?.planName || null,
    price: latest?.price ?? latest?.plan_price ?? null,
    start,
    end,
    isActive,
    status: cancelled ? 'Cancelled' : isActive ? 'Active' : 'Expired',
    daysLeft: isActive ? Math.max(0, Math.ceil((endTime - Date.now()) / DAY_MS)) : 0,
  };
};

const withMobileNo = (user) => (user ? { ...user, mobileNo: user.mobileNo ?? user.mobile_no } : user);

function Profile() {
  const [profileData, setProfileData] = useState({});
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const authToken = localStorage.getItem("token");
  const { isLoggedIn, logout } = useContext(AuthContext);

  useEffect(() => {
    if (authToken) {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${authToken}`);

      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow"
      };

      fetch(`${process.env.REACT_APP_API_BASE_URL}users/me`, requestOptions)
        .then((response) => {
          if (response.status === 401) setSessionExpired(true);
          return response.json();
        })
        .then(async (result) => {
          const profile = withMobileNo(result?.data) || {};
          setProfileData(profile);
          // Subscription: active plan + history (each may fail on its own)
          const [active, history] = await Promise.all([
            PlanServices.getActiveSubscription().then((r) => r?.data || null).catch(() => null),
            PlanServices.getSubscriptionHistory().then((r) => r?.data || []).catch(() => []),
          ]);
          setSubscription(pickSubscription(profile, active, history));
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching profile:', error);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [authToken]);

  const handleUpdateProfile = (e) => {
    e.preventDefault();

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${authToken}`);

    const raw = JSON.stringify({
      name: profileData?.name,
      email: profileData?.email,
      mobileNo: profileData?.mobileNo,
      status: "active",
      state: profileData?.state,
      city: profileData?.city,
      district: profileData?.district,
    });

    const requestOptions = {
      method: "PUT",
      headers: myHeaders,
      body: raw,
      redirect: "follow"
    };

    fetch(`${process.env.REACT_APP_API_BASE_URL}users/me`, requestOptions)
      .then(response => response.json())
      .then(result => {
        setProfileData(withMobileNo(result.data));
        console.log('Profile updated successfully');
        alert("Profile updated successfully");
      })
      .catch(error => console.error('Error updating profile:', error));
  };

  const LogOut = () => {
    localStorage.clear()
    window.location.href = "http://localhost:3000";
    logout(); // Call the logout function from AuthContext
  };

  if (!authToken || !isLoggedIn) {
    // Redirect to login if not logged in
    return <Navigate to="/" />;
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <section className="profile-section py-70">
      <div className="container">
        <div className="profile-section-container">
          <div className="card">
            <div className="card-header">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="user-details-left">
                    <div className="user-img">
                      <img src="img/user-img.svg" alt="user-img" />
                      <button><i className="fa fa-edit" /></button>
                    </div>
                    <div className="user-name">
                      <h5>{profileData?.name}</h5>
                      <button data-bs-toggle="modal" data-bs-target="#updateModel">
                        <i className="fa fa-edit me-2" />Edit Profile
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="personal-info-sec">
                    <h6>Personal info</h6>
                    <ul className="list-unstyled">
                      <li><span><i className="fa fa-envelope me-2" /></span>{profileData?.email}</li>
                      <li><span><i className="fa fa-phone me-2" /></span>{profileData?.mobileNo}</li>
                      <li><span><i className="fa fa-map-marker me-2" /></span>{profileData?.city}, {profileData?.state}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body">
              {sessionExpired && (
                <div className="alert alert-warning text-center">
                  Your session has expired. Please log in again to see your profile.
                </div>
              )}
              <div className="row g-3 profile-detail-grid">
                {/* Account details (from signup and later edits) */}
                <div className="col-lg-6">
                  <div className="profile-box">
                    <h4>Account details</h4>
                    <dl>
                      <div><dt>Name</dt><dd>{shown(profileData?.name)}</dd></div>
                      <div><dt>Email</dt><dd>{shown(profileData?.email)}</dd></div>
                      <div><dt>Mobile</dt><dd>{shown(profileData?.mobileNo)}</dd></div>
                      <div><dt>Address</dt><dd>{shown(profileData?.address)}</dd></div>
                      <div><dt>City</dt><dd>{shown(profileData?.city)}</dd></div>
                      <div><dt>District</dt><dd>{shown(profileData?.district)}</dd></div>
                      <div><dt>State</dt><dd>{shown(profileData?.state)}</dd></div>
                      <div><dt>Member since</dt><dd>{fmtDate(profileData?.created_at || profileData?.createdAt)}</dd></div>
                    </dl>
                  </div>
                </div>

                {/* Subscription */}
                <div className="col-lg-6">
                  <div className="profile-box">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h4 className="mb-0">Subscription</h4>
                      {subscription && (
                        <span className={`sub-badge ${subscription.isActive ? 'sub-active' : 'sub-expired'}`}>
                          {subscription.status}
                        </span>
                      )}
                    </div>
                    {subscription ? (
                      <dl>
                        {subscription.planName && <div><dt>Plan</dt><dd>{subscription.planName}</dd></div>}
                        {subscription.price != null && <div><dt>Price paid</dt><dd>₹{subscription.price}</dd></div>}
                        <div><dt>Start date</dt><dd>{fmtDate(subscription.start)}</dd></div>
                        <div><dt>Expiry date</dt><dd>{fmtDate(subscription.end)}</dd></div>
                        {subscription.isActive && (
                          <div><dt>Remaining</dt><dd>{subscription.daysLeft} day{subscription.daysLeft === 1 ? '' : 's'}</dd></div>
                        )}
                      </dl>
                    ) : (
                      <p className="text-muted mb-3">You don&apos;t have a subscription yet.</p>
                    )}
                    {!subscription?.isActive && (
                      <Link to="/pricing" className="site-btn btn-blue d-inline-block mt-2">
                        {subscription ? 'Renew subscription' : 'View plans'}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="card-footer">
              <div className="donate-btn-grp text-center py-4">
                <button className="site-btn btn-blue logout" onClick={LogOut}>Logout</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* ======== Update Profile popup ======== */}
      <div className="modal fade" id="updateModel" tabIndex={-1} aria-labelledby="updateModelLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="disclaimerModelLabel">Update Profile</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body py-4 px-5">
              <div className="header-logo text-center">
                <a href="#"><img src="img/header-logo.svg" alt="logo" /></a>
              </div>
              <div className="login-form-outer">
                <form onSubmit={handleUpdateProfile}>
                  <div className="form-input mb-3">
                    <input 
                      type="text" 
                      name="Name" 
                      className="form-control" 
                      placeholder="Name" 
                      value={profileData?.name || ''} 
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })} 
                    />
                  </div>
                  
                  <div className="form-input mb-3">
                    <input 
                      type="text" 
                      name="State" 
                      className="form-control" 
                      placeholder="State" 
                      value={profileData?.state || ''} 
                      onChange={(e) => setProfileData({ ...profileData, state: e.target.value })} 
                    />
                  </div>
                  <div className="form-input mb-3">
                    <input 
                      type="text" 
                      name="District" 
                      className="form-control" 
                      placeholder="District" 
                      value={profileData?.district || ''} 
                      onChange={(e) => setProfileData({ ...profileData, district: e.target.value })} 
                    />
                  </div>
                  <div className="form-input mb-3">
                    <input 
                      type="text" 
                      name="City" 
                      className="form-control" 
                      placeholder="City" 
                      value={profileData?.city || ''} 
                      onChange={(e) => setProfileData({ ...profileData, city: e.target.value })} 
                    />
                  </div>
                  <div className="form-input mb-3">
                    <input 
                      type="text" 
                      name="Phone" 
                      className="form-control" 
                      placeholder="Phone" 
                      value={profileData?.mobileNo || ''} 
                      onChange={(e) => setProfileData({ ...profileData, mobileNo: e.target.value })} 
                    />
                  </div>
                  <div className="update-btn-outer">
                    <button type="button" className="site-btn btn-blue-o" data-bs-dismiss="modal" aria-label="Close">Cancel</button>
                    <button type="submit" className="update-btn site-btn btn-blue">Update</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
}

export default Profile;
